export interface GpsCoordinates {
  latitude: number;
  longitude: number;
}

function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const trimmed = value.trim();
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }

    const fraction = trimmed.match(/^([+-]?\d+(?:\.\d+)?)\s*\/\s*([+-]?\d+(?:\.\d+)?)$/);
    if (fraction) {
      const numerator = Number(fraction[1]);
      const denominator = Number(fraction[2]);

      if (Number.isFinite(numerator) && Number.isFinite(denominator) && denominator !== 0) {
        return numerator / denominator;
      }
    }
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const numerator = coerceFiniteNumber(record.numerator ?? record.num ?? record.n);
    const denominator = coerceFiniteNumber(record.denominator ?? record.den ?? record.d);

    if (numerator != null && denominator != null && denominator !== 0) {
      return numerator / denominator;
    }
  }

  return null;
}

function getDirectionSign(ref: unknown): number {
  if (typeof ref !== 'string') {
    return 1;
  }

  const direction = ref.trim().toUpperCase();
  if (!direction) {
    return 1;
  }

  if (direction === 'S' || direction === 'W' || direction.includes('SOUTH') || direction.includes('WEST')) {
    return -1;
  }

  return 1;
}

export function normalizeNumber(value: unknown): number | null {
  return coerceFiniteNumber(value);
}

export function normalizeCoordinate(value: unknown, min: number, max: number): number | null {
  const parsed = coerceFiniteNumber(value)
    ?? (typeof value === 'string' ? parseCoordinateText(value) : null);

  if (parsed == null || parsed < min || parsed > max) {
    return null;
  }

  return parsed;
}

function parseCoordinateText(value: string): number | null {
  const text = value.trim().toUpperCase();
  if (!text) return null;

  const direction = text.match(/[NSEW]/)?.[0] ?? '';
  const directionSign = direction === 'S' || direction === 'W' ? -1 : 1;
  const normalized = text
    .replace(/[NSEW]/g, ' ')
    .replace(/[^0-9.+-]+/g, ' ')
    .trim();

  if (!normalized) return null;

  const parts = normalized
    .split(/\s+/)
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part));

  if (parts.length === 0) return null;

  if (parts.length === 1) {
    const absolute = Math.abs(parts[0]);
    return direction ? directionSign * absolute : parts[0];
  }

  const degrees = Math.abs(parts[0]);
  const minutes = Math.abs(parts[1]);
  const seconds = Math.abs(parts[2] ?? 0);
  const valueSign = parts[0] < 0 ? -1 : 1;
  const sign = direction ? directionSign : valueSign;

  return sign * (degrees + minutes / 60 + seconds / 3600);
}

export function dmsToDecimal(value: unknown, ref: unknown): number | null {
  if (typeof value === 'string') {
    const direction = typeof ref === 'string' ? ref : '';
    return parseCoordinateText(`${value} ${direction}`);
  }

  if (!Array.isArray(value) || value.length < 3) {
    return null;
  }

  const degrees = normalizeNumber(value[0]);
  const minutes = normalizeNumber(value[1]);
  const seconds = normalizeNumber(value[2]);

  if (degrees == null || minutes == null || seconds == null) {
    return null;
  }

  const sign = getDirectionSign(ref);

  return sign * (Math.abs(degrees) + minutes / 60 + seconds / 3600);
}

function firstPresent(data: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (data[key] != null) {
      return data[key];
    }
  }

  return null;
}

function objectScope(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function extractGpsFromScope(data: Record<string, unknown>): GpsCoordinates | null {
  const latitudeValue = firstPresent(data, [
    'latitude',
    'Latitude',
    'GPSLatitude',
    'gpsLatitude',
    'GPS Latitude',
    'exif:GPSLatitude',
  ]);
  const longitudeValue = firstPresent(data, [
    'longitude',
    'Longitude',
    'GPSLongitude',
    'gpsLongitude',
    'GPS Longitude',
    'exif:GPSLongitude',
  ]);
  const latitudeRef = firstPresent(data, [
    'GPSLatitudeRef',
    'gpsLatitudeRef',
    'LatitudeRef',
    'GPS Latitude Ref',
    'exif:GPSLatitudeRef',
  ]);
  const longitudeRef = firstPresent(data, [
    'GPSLongitudeRef',
    'gpsLongitudeRef',
    'LongitudeRef',
    'GPS Longitude Ref',
    'exif:GPSLongitudeRef',
  ]);

  const latitude = normalizeCoordinate(latitudeValue, -90, 90)
    ?? normalizeCoordinate(dmsToDecimal(latitudeValue, latitudeRef), -90, 90);
  const longitude = normalizeCoordinate(longitudeValue, -180, 180)
    ?? normalizeCoordinate(dmsToDecimal(longitudeValue, longitudeRef), -180, 180);

  if (latitude != null && longitude != null) {
    return { latitude, longitude };
  }

  return null;
}

export function extractGpsFromParsedExif(data: Record<string, unknown>): GpsCoordinates | null {
  const scopes = [
    data,
    objectScope(data.gps),
    objectScope(data.exif),
    objectScope(data.ifd0),
    objectScope(data.xmp),
  ].filter((scope): scope is Record<string, unknown> => scope != null);

  for (const scope of scopes) {
    const gps = extractGpsFromScope(scope);
    if (gps) return gps;
  }

  return null;
}
